import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, FormInstance, Radio, Space, Tooltip } from 'antd';
import {
  IAccessRightsNumbers,
  PermissionsEnum,
} from 'app/containers/User/slice/types';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPermissionCheckboxes } from '../../slice/helpers';

interface IProps {
  form: FormInstance<any>;
  isOpen: boolean;
  checkedPermissions?: IAccessRightsNumbers | null;
}
const permissionsWithRequiredFieldView = [
  PermissionsEnum.taskAdministration,
  PermissionsEnum.cultureAdministration,
  PermissionsEnum.resourceAdministration,
  PermissionsEnum.recommendationPermission,
];

const PermissionsRadioButtons: FC<IProps> = ({
  form,
  isOpen,
  checkedPermissions,
}) => {
  const { t } = useTranslation();
  const [disabledFields, setDisabledFields] = useState<PermissionsEnum[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (checkedPermissions?.fieldAdministration === 0) {
      // Disable dependent radio selects and set value 0
      setDisabledFields(permissionsWithRequiredFieldView);
      form?.setFieldsValue({
        [PermissionsEnum.taskAdministration]: 0,
        [PermissionsEnum.cultureAdministration]: 0,
        [PermissionsEnum.resourceAdministration]: 0,
      });
    } else {
      // Endable dependent radio selects
      setDisabledFields(prevState =>
        prevState.filter(
          permission =>
            !permissionsWithRequiredFieldView.some(p => p === permission),
        ),
      );
    }
    if (
      checkedPermissions?.fieldAdministration === 0 ||
      checkedPermissions?.taskAdministration === 0
    ) {
      // Disable recommendation radio select and set recommendation value 0
      setDisabledFields(prevState => {
        if (
          prevState.some(
            prevStatePermission =>
              prevStatePermission === PermissionsEnum.recommendationPermission,
          )
        ) {
          return prevState;
        }
        return [...prevState, PermissionsEnum.recommendationPermission];
      });

      form?.setFieldsValue({
        [PermissionsEnum.recommendationPermission]: 0,
      });
    } else {
      // Endable recommendation radio select
      setDisabledFields(prevState =>
        prevState.filter(
          permission => permission !== PermissionsEnum.recommendationPermission,
        ),
      );
    }
  }, [checkedPermissions, form, isOpen]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      setDisabledFields([]);
    }
  }, [isOpen, form]);

  const checkboxesRows = getPermissionCheckboxes();

  return (
    <>
      {checkboxesRows.map(row => (
        <Form.Item
          key={row.name}
          label={
            <>
              {Boolean(
                disabledFields.includes(row.name) &&
                  row?.requiredPermissions?.length,
              ) ? (
                <Space>
                  {row.label}
                  <Tooltip
                    title={
                      <>
                        <div>
                          {t(
                            'User.RequiredPermissions',
                            'Required permissions:',
                          )}
                        </div>
                        {row?.requiredPermissions?.map(permission => (
                          <div key={permission}>{permission}</div>
                        ))}
                      </>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              ) : (
                row.label
              )}
            </>
          }
          name={row.name}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Radio.Group disabled={disabledFields.includes(row.name)}>
            <Radio.Button value={0}>
              {t('User.AddUserForm.Off', 'Off')}
            </Radio.Button>
            {!row.isVieWDisabled && (
              <Radio.Button value={1} disabled={row.isVieWDisabled}>
                {t('User.AddUserForm.ViewOnly', 'View only')}
              </Radio.Button>
            )}
            {!row.isEditDisabled && (
              <Radio.Button value={2} disabled={row.isEditDisabled}>
                {t(
                  'User.AddUserForm.ViewAndEdit',
                  'View, create, edit and delete',
                )}
              </Radio.Button>
            )}
          </Radio.Group>
        </Form.Item>
      ))}
    </>
  );
};

export default PermissionsRadioButtons;
